import React, { useEffect, useState } from "react";
import { Card, Row, Col, Button, Progress, Tag, Spin, Empty, theme } from "antd";
import {
  CaretUpOutlined,
  TeamOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  RightOutlined
} from "@ant-design/icons";
import { TbBrowser, TbUsersGroup, TbCurrencyDollar, TbBarrierBlock } from "react-icons/tb";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import { isAuthenticated } from "../../../commons/Auth/auth";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import api from "../../../api/api";

import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { chartColors, apexBase } from "../../../design-system/theme";
import "./Dashboard.css";

// One KPI tile. Clickable tiles navigate; the whole tile is the touch target.
const KpiTile = ({ title, value, icon, tone = "brand", sub, onClick }) => (
  <Card
    className={`sp-dashboard__kpi sp-dashboard__kpi--${tone}`}
    hoverable={Boolean(onClick)}
    onClick={onClick}
    role={onClick ? "link" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? e => {
            if (e.key === "Enter") onClick();
          }
        : undefined
    }
  >
    <span className="sp-dashboard__kpi-icon">{icon}</span>
    <div className="sp-dashboard__kpi-title">{title}</div>
    <div className="sp-dashboard__kpi-value">{value}</div>
    {sub && <div className="sp-dashboard__kpi-sub">{sub}</div>}
  </Card>
);

// "Needs attention" chip in the welcome banner.
const AttentionChip = ({ icon, label, value, onClick, alert }) => (
  <button
    type="button"
    className={`sp-dashboard__chip${alert && value > 0 ? " is-alert" : ""}`}
    onClick={onClick}
  >
    <span className="sp-dashboard__chip-icon">{icon}</span>
    <span className="sp-dashboard__chip-value">{value}</span>
    <span className="sp-dashboard__chip-label">{label}</span>
    <RightOutlined className="sp-dashboard__chip-go" aria-hidden="true" />
  </button>
);

const ChartEmpty = () => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className="sp-dashboard__chart-empty" />
);

const Dashboard = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const router = useHistory();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [recentActivities, setRecentActivities] = useState({});
  const [inventoryData, setInventoryData] = useState([]);

  const user = isAuthenticated();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const serviceProvider = isAuthenticated()?.serviceProviderId; // Replace with actual ID

      // Fetch all dashboard data
      const [statsRes, statusRes, deptRes, earningsRes, activitiesRes, inventoryRes] =
        await Promise.all([
          axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/dashboard/stats`,
            { serviceProvider },
            { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
          ),
          axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/dashboard/project-status`,
            { serviceProvider },
            { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
          ),
          axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/dashboard/employee-departments`,
            { serviceProvider },
            { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
          ),
          api.post(
            `${process.env.REACT_APP_BACKEND_URL}/dashboard/monthly-earnings`,
            { serviceProvider },
            { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
          ),
          axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/dashboard/recent-activities`,
            { serviceProvider },
            { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
          ),
          axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/inventory/filters`,
            { serviceProvider, pageNo: 0, pageSize: 1000000000 },
            { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
          )
        ]);
      setDashboardData(statsRes.data.data);
      setProjectStatusData(statusRes.data.data);
      setDepartmentData(deptRes.data.data);
      setEarningsData(earningsRes.data.data);
      setRecentActivities(activitiesRes.data.data);
      setInventoryData(
        inventoryRes.data.data?.filter(data => data?.minimumAmount >= data?.total - data?.used)
      );
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations (colors from the antd theme; sizes follow the container)
  const chartHeight = isMobile ? 260 : 300;
  const pieChartOptions = {
    ...apexBase,
    chart: { ...apexBase.chart, type: "donut" },
    labels: projectStatusData.map(item => t(item._id)),
    colors: chartColors,
    stroke: { width: 2, colors: [token.colorBgContainer] },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            total: { show: true, label: t("Total"), color: token.colorTextSecondary }
          }
        }
      }
    },
    dataLabels: { enabled: false },
    legend: { ...apexBase.legend, position: "bottom" }
  };

  const barChartOptions = {
    ...apexBase,
    chart: { ...apexBase.chart, type: "bar" },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
        barHeight: "60%"
      }
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: departmentData.map(item => item._id || t("Unassigned"))
    },
    colors: [chartColors[0]]
  };
  // Horizontal bars: grow with the number of departments instead of squeezing them.
  const barChartHeight = Math.max(chartHeight, departmentData.length * 36 + 60);

  const lineChartOptions = {
    ...apexBase,
    chart: { ...apexBase.chart, type: "area" },
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { opacityFrom: 0.28, opacityTo: 0.02 } },
    dataLabels: { enabled: false },
    markers: { size: 0, hover: { size: 5 } },
    xaxis: {
      categories: earningsData.map(item => item.month)
    },
    yaxis: {
      title: { text: isMobile ? undefined : t("Earnings ($)") }
    },
    colors: [chartColors[0]]
  };

  const recentProjectColumns = [
    {
      title: t("Project Name"),
      dataIndex: "projectName",
      key: "projectName",
      mobile: "title"
    },
    {
      title: t("Client"),
      dataIndex: "client",
      key: "client",
      render: client => (client ? `${client.firstName} ${client.lastName}` : t("N/A"))
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      mobile: "subtitle",
      render: status => (
        <Tag
          color={
            status === "COMPLETED"
              ? "green"
              : status === "IN PROGRESS"
                ? "blue"
                : status === "ISSUES"
                  ? "red"
                  : "default"
          }
        >
          {t(status)}
        </Tag>
      )
    },
    {
      title: t("Progress"),
      dataIndex: "progress",
      key: "progress",
      render: progress => (
        <Progress percent={parseInt(progress)} size="small" className="sp-dashboard__progress" />
      )
    }
  ];

  const recentInvoiceColumns = [
    {
      title: t("Title"),
      dataIndex: "title",
      key: "title",
      mobile: "title"
    },
    {
      title: t("Amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: amount => `$${parseFloat(amount || 0).toFixed(2)}`
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      mobile: "subtitle",
      render: status => <Tag color={status === "Paid" ? "green" : "orange"}>{t(status)}</Tag>
    }
  ];

  if (loading) {
    return (
      <div className="sp-dashboard sp-dashboard--loading">
        <Spin size="large" />
      </div>
    );
  }

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? t("Good morning") : hour < 18 ? t("Good afternoon") : t("Good evening");
  const today = new Intl.DateTimeFormat(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long"
  }).format(new Date());
  const earnings = dashboardData.earnings?.total || 0;
  const money = v =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: v >= 10000 ? 0 : 2
    }).format(v || 0);

  return (
    <div className="sp-dashboard">
      {/* Welcome + what needs attention */}
      <section className="sp-dashboard__hero">
        <div className="sp-dashboard__hero-text">
          <span className="sp-dashboard__date">{today}</span>
          <h2>
            {greeting}, {user?.firstName || ""}
          </h2>
          <p>{t("Here is what needs your attention today.")}</p>
        </div>
        <div className="sp-dashboard__chips">
          <AttentionChip
            icon={<ClockCircleOutlined />}
            label={t("Pending Invoices")}
            value={dashboardData.invoices?.pending || 0}
            onClick={() => router.push("/user/invoices")}
          />
          <AttentionChip
            alert
            icon={<ExclamationCircleOutlined />}
            label={t("Overdue Invoices")}
            value={dashboardData.invoices?.overdue || 0}
            onClick={() => router.push("/user/invoices")}
          />
          <AttentionChip
            alert
            icon={<TbBarrierBlock />}
            label={t("Inventory Out Of Stock")}
            value={inventoryData?.length || 0}
            onClick={() => router.push("/user/inventory")}
          />
          <AttentionChip
            icon={<FileTextOutlined />}
            label={t("Total Invoices")}
            value={dashboardData.invoices?.total || 0}
            onClick={() => router.push("/user/invoices")}
          />
        </div>
      </section>

      {/* KPIs: 2-up on phones, 4-up on desktop */}
      <Row gutter={[12, 12]} className="sp-dashboard__row">
        <Col xs={12} lg={6}>
          <KpiTile
            title={t("Total Projects")}
            value={dashboardData.projects?.total || 0}
            icon={<TbBrowser />}
            tone="brand"
            onClick={() => router.push("/user/projects")}
            sub={
              <>
                <span className="sp-dashboard__up">
                  <CaretUpOutlined /> {Math.abs(dashboardData.projects?.change || 0)}%
                </span>{" "}
                <span className="sp-dashboard__muted">{t("vs last month")}</span>
              </>
            }
          />
        </Col>
        <Col xs={12} lg={6}>
          <KpiTile
            title={t("Total Employees")}
            value={dashboardData.employees?.total || 0}
            icon={<TeamOutlined />}
            tone="green"
            onClick={() => router.push("/user/user")}
            sub={
              <span className="sp-dashboard__muted">
                {dashboardData.employees?.active || 0} {t("Active")}
              </span>
            }
          />
        </Col>
        <Col xs={12} lg={6}>
          <KpiTile
            title={t("Total Clients")}
            value={dashboardData.clients?.total || 0}
            icon={<TbUsersGroup />}
            tone="violet"
            onClick={() => router.push("/user/clients")}
            sub={<span className="sp-dashboard__muted">{t("Active clients")}</span>}
          />
        </Col>
        <Col xs={12} lg={6}>
          <KpiTile
            title={t("Total Earnings")}
            value={<span className="sp-dashboard__money">{money(earnings)}</span>}
            icon={<TbCurrencyDollar />}
            tone="amber"
            sub={
              <span className="sp-dashboard__muted">
                {money(dashboardData.earnings?.pending || 0)} {t("Pending")}
              </span>
            }
          />
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[16, 16]} className="sp-dashboard__row">
        <Col xs={24} lg={12}>
          <Card title={t("Project Status Distribution")} className="sp-dashboard__chart">
            {projectStatusData.length > 0 ? (
              <ReactApexChart
                options={pieChartOptions}
                series={projectStatusData.map(item => item.count)}
                type="donut"
                width="100%"
                height={chartHeight}
              />
            ) : (
              <ChartEmpty />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t("Employees by Department")} className="sp-dashboard__chart">
            {departmentData.length > 0 ? (
              <ReactApexChart
                options={barChartOptions}
                series={[{ name: t("Employees"), data: departmentData.map(item => item.count) }]}
                type="bar"
                width="100%"
                height={barChartHeight}
              />
            ) : (
              <ChartEmpty />
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title={t("Monthly Earnings")} className="sp-dashboard__chart">
            {earningsData.length > 0 ? (
              <ReactApexChart
                options={lineChartOptions}
                series={[{ name: t("Earnings"), data: earningsData.map(item => item.earnings) }]}
                type="area"
                width="100%"
                height={chartHeight}
              />
            ) : (
              <ChartEmpty />
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Activities */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={t("Recent Projects")}
            className="sp-dashboard__list"
            extra={
              <Button onClick={() => router.push("/user/projects")} type="link">
                {t("View All")}
              </Button>
            }
          >
            <ResponsiveTable
              columns={recentProjectColumns}
              dataSource={recentActivities.recentProjects || []}
              pagination={false}
              size="small"
              rowKey="_id"
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t("Recent Invoices")}
            className="sp-dashboard__list"
            extra={
              <Button onClick={() => router.push("/user/invoices")} type="link">
                {t("View All")}
              </Button>
            }
          >
            <ResponsiveTable
              columns={recentInvoiceColumns}
              dataSource={recentActivities.recentInvoices || []}
              pagination={false}
              size="small"
              rowKey="_id"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
